import { useEffect, useState } from "react";

import { Address } from "@/Common/Api/Contacts/Dto/Address";
import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { PreventiveCareNotice } from "@/Common/Api/PreventiveCare/Dto/Responses/PreventiveCareNotice";
import { Modal } from "@/Common/Components/Modals/Modal";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { NameHelper } from "@/Common/Helpers/NameHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { Guid } from "@/Common/Models/Guid";
import { PreventiveCareNoticeLetterTypeEnum } from "@/Common/Models/PreventiveCare/PreventiveCareNoticeLetterTypeEnum";
import { PreventiveRecallTab, RedirectTo } from "@/Common/Models/UI/Enums";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { IconButton } from "@/DesignSystem";
import { PreventiveRecallListTableItem } from "../Models/MyNotesInboxTableItem";
import { IPreventiveRecallListModel } from "../Models/UI/IPreventiveRecallListModel";
import { CorrespondenceHistory } from "./PreventiveRecallList/CorrespondenceHistory";
import { GenerateBonusReportPopup } from "./GenerateBonusReportPopup";
import { PhoneCallNoticePopup } from "./PhoneCallNoticePopup";
import { PortfolioSaveOptionKey } from "./PortfolioSaveOptionKey";
import { PreventiveCareCriteriaPopup } from "./PreventiveCareCriteriaPopup";
import { PreventiveRecallListSettingsContainer } from "./PreventiveRecallListSettingsContainer";

import styles from "./PreventiveRecallList.module.scss";

import Info from "@/DesignSystem/Atoms/Icons/Types/Info/info-fill.svg";
import img_loading from "@/Globals/_images/loading.gif";

interface IPreventiveRecallListProps {
    model: IPreventiveRecallListModel;
}

const helper = new MyleHelper();
const saveOptionKey = PortfolioSaveOptionKey.PreventiveRecallList;
const FieldNames = {
    Checkbox: "RecallsCheckbox",
    Provider: "RecallsProvider",
    Patient: "RecallsPatient",
    PreventiveItem: "RecallsPreventiveItem",
    HealthCareNumber: "RecallsHCN",
    Age: "RecallsAge",
    SexAtBirth: "RecallsSexAtBirth",
    PhoneNumber: "RecallsPhoneNumber",
    Address: "RecallsAddress",
    Notice: "RecallsNotice",
    LastProcedureDate: "RecallsLastProcedureDate",
    LastCommunicationDate: "RecallsLastCommunicationDate",
    Action: "RecallsAction"
};

function PreventiveRecallList(props: Readonly<IPreventiveRecallListProps>): JSX.Element {
    const { getText } = useTranslation();
    const [selectedNotice, setSelectedNotice] = useState<null | PreventiveCareNotice>(null);
    const currentTab = props.model.get("CurrentTab");
    const hasAvailableCheckboxes = portfolioStore.preventiveRecallListModel.get("RecallList").some(item => !item.CheckboxDisabled);
    const settings = props.model.get("SavedSettingsContainerState");
    const selectedProviders = ArrayHelper.SortDynamic((settings.globalProvidersList || []).filter(p => p.Checked), "Name");
    const eligibleCriteriaList = cacheStore.PreventiveCareCriteriaList.filter(p => p.EligibleForBonus);

    const columnsOptions = helper.loadTableColumnOptionsFromSettings(
        saveOptionKey,
        getMyleTableColumnsDefaultOptions()
    );

    const tableColumns = getTableColumns(columnsOptions);
    const tableRenderOptions = loadTableRenderOptions(columnsOptions);
    const columnConfig = helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());

    const toBeGeneratedLetters = portfolioStore.preventiveRecallListModel.get("RecallList").filter(item => item.IsSelected);

    function getMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        return [
            new MyleTableColumnOptions({
                HeaderText: "",
                FieldName: FieldNames.Checkbox,
                WidthPixels: 38,
                IsFixedInPlace: true
            }),
            new MyleTableColumnOptions({
                HeaderText: getText("Staff1Label"),
                FieldName: FieldNames.Provider,
                WidthPercentageDefault: 10
            }),
            new MyleTableColumnOptions({
                HeaderText: getText("PatientLabel"),
                FieldName: FieldNames.Patient,
                WidthPercentageDefault: 12
            }),
            new MyleTableColumnOptions({
                HeaderText: getText("RecallList_ItemLabel"),
                FieldName: FieldNames.PreventiveItem,
                WidthPercentageDefault: 8
            }),
            new MyleTableColumnOptions({
                HeaderText: getText("RecallList_HealthCardLabel"),
                FieldName: FieldNames.HealthCareNumber,
                WidthPercentageDefault: 8
            }),
            new MyleTableColumnOptions({
                HeaderText: getText("AgeLabel"),
                FieldName: FieldNames.Age,
                WidthPercentageDefault: 4
            }),
            new MyleTableColumnOptions({
                HeaderText: getText("SexAtBirthLabel"),
                FieldName: FieldNames.SexAtBirth,
                WidthPercentageDefault: 8,
                IsHidden: true
            }),
            new MyleTableColumnOptions({
                HeaderText: getText("PhoneNumberLabel"),
                FieldName: FieldNames.PhoneNumber,
                WidthPercentageDefault: 8
            }),
            new MyleTableColumnOptions({
                HeaderText: getText("AddressLabel"),
                FieldName: FieldNames.Address,
                WidthPercentageDefault: 10,
                IsHidden: true
            }),
            new MyleTableColumnOptions({
                HeaderText: getText("RecallList_NextNoticeLabel"),
                FieldName: FieldNames.Notice,
                WidthPercentageDefault: 8,
                IsHidden: true
            }),
            new MyleTableColumnOptions({
                HeaderText: getText("RecallList_LastProcedureLabel"),
                FieldName: FieldNames.LastProcedureDate,
                WidthPercentageDefault: 8
            }),
            new MyleTableColumnOptions({
                HeaderText: getText("RecallList_LastCommunicationLabel"),
                FieldName: FieldNames.LastCommunicationDate,
                WidthPercentageDefault: 8
            }),
            new MyleTableColumnOptions({
                HeaderText: getText("ActionLabel"),
                FieldName: FieldNames.Action,
                WidthPercentageDefault: 4,
                IsHidden: true
            })
        ];
    }

    function getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<PreventiveRecallListTableItem>(
            columnsOptions,
            column => {
                switch (column.FieldName) {
                    case FieldNames.Checkbox:
                        return {
                            renderCell: item => {
                                if (item.CheckboxDisabled)
                                    return null;

                                return (
                                    <div>
                                        <input
                                            type="checkbox"
                                            id={item.Model.Id}
                                            onChange={e => sessionStore.dispatch(LocalOperations.Portfolio.PreventiveRecallList_SelectItem, { id: item.Model.Id, isSelected: e.currentTarget.checked })}
                                            checked={item.IsSelected}
                                        />
                                    </div>
                                );
                            },
                            renderHeader: () => (
                                <div>
                                    <input
                                        type="checkbox"
                                        onChange={e => sessionStore.dispatch(LocalOperations.Portfolio.PreventiveRecallList_SelectAllItems, { isSelected: e.currentTarget.checked })}
                                        checked={props.model.get("IsAllItemsSelected")}
                                        disabled={!hasAvailableCheckboxes}
                                    />
                                </div>
                            )
                        };

                    case FieldNames.Provider:
                        return {
                            renderCell: item => (
                                <div>
                                    {MyleHelper.ReplaceEmptyString(Employee.getFullNameWithPrefixContactFacet(item.Model.EmployeeContactFacetId))}
                                </div>
                            )
                        };

                    case FieldNames.Patient:
                        return {
                            renderCell: item => (
                                <div>
                                    <a onClick={() => onPatientClick(item.Model.PatientContactId)}>
                                        {NameHelper.formatFullNameForHasPatientName(item.Model)}
                                    </a>
                                </div>
                            )
                        };

                    case FieldNames.HealthCareNumber:
                        return {
                            renderCell: item => (
                                <div>
                                    {item.Model.HealthCareNumber}
                                </div>
                            )
                        };

                    case FieldNames.Age:
                        return {
                            renderCell: item => (
                                <div>
                                    {getPatientAge(item.Model.DateOfBirth)}
                                </div>
                            )
                        };

                    case FieldNames.Address:
                        return {
                            renderCell: item => (
                                <div>
                                    {Address.GetFullAddress(item.Model.Address)}
                                </div>
                            )
                        };

                    case FieldNames.SexAtBirth:
                        return {
                            renderCell: item => {
                                const gender = cacheStore.GetLocalizedValueByLookupId(item.Model.GenderLookupId);

                                return (
                                    <div>
                                        {gender[0] ?? gender}
                                    </div>
                                );
                            }
                        };

                    case FieldNames.PhoneNumber:
                        return {
                            renderCell: item => (
                                <div>
                                    {item.Model.PhoneNumber ? PhoneNumber.stringFormattedPhone(item.Model.PhoneNumber) : ""}
                                </div>
                            )
                        };

                    case FieldNames.PreventiveItem:
                        return {
                            renderCell: item => (
                                <div>
                                    {cacheStore.findPreventiveCareCriteria(item.Model.PreventiveCareCriteriaId)?.getTranslatedName()}
                                </div>
                            )
                        };

                    case FieldNames.Notice:
                        return {
                            renderCell: item => {
                                const letterLabel = getUpcomingPreventiveCareNoticeLabel(item.Model.LetterType);

                                return (
                                    <div>
                                        {item.Model.LetterType == PreventiveCareNoticeLetterTypeEnum.SecondLetter
                                           || item.Model.LetterType == PreventiveCareNoticeLetterTypeEnum.PhoneCall ? (
                                               <a
                                                   onClick={() => setSelectedNotice(new PreventiveCareNotice({
                                                       PreventiveCareCriteriaId: item.Model.PreventiveCareCriteriaId,
                                                       EmployeeContactFacetId: item.Model.EmployeeContactFacetId,
                                                       PatientContactFacetId: item.Model.PatientContactFacetId
                                                   }))}
                                               >{letterLabel}
                                               </a>
                                            ) : letterLabel
                                        }
                                    </div>
                                );
                            }
                        };

                    case FieldNames.LastProcedureDate:
                        return {
                            renderCell: item => {
                                if (!item.Model.LastProcedureDate)
                                    return null;

                                return <div>{LuxonDateTimeParser.parse(item.Model.LastProcedureDate).toFormat("yyyy/LL/dd")}</div>;
                            }
                        };

                    case FieldNames.LastCommunicationDate:
                        return {
                            renderCell: item => {
                                if (!item.Model.LastCommunicationDate)
                                    return null;

                                return <div>{LuxonDateTimeParser.parse(item.Model.LastCommunicationDate).toFormat("yyyy/LL/dd")}</div>;
                            }
                        };

                    case FieldNames.Action:
                        return {
                            renderCell: item => (
                                <div className={styles.infoIconWrapper}>
                                    <IconButton
                                        category="status-icon"
                                        disabled={item.Model.LetterType === PreventiveCareNoticeLetterTypeEnum.None}
                                        id={`${item.Model.Id}_info_icon_button`}
                                        onClick={() => sessionStore.dispatch(LocalOperations.Portfolio.PreventiveRecallList_LoadPreventiveCareNotices, { model: item.Model })}
                                    >
                                        <Info />
                                    </IconButton>
                                </div>
                            )
                        };
                }
            },
            false
        );
    }

    function loadTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        return Immutable.fromJS({
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: true,
            LastColumnNoDrag: false,
            Autoresize: true,
            OnColumnWidthChange: null
        });
    }

    function onPatientClick(patientContactId: string): void {
        sessionStore.dispatch(LocalOperations.Misc.RedirectToTab, {
            value: patientContactId,
            target: RedirectTo.Patient,
            currentLocation: RouterHistory.location
        });
    }

    function getPageSize(): number {
        return settings?.pageSize ?? cacheStore.getDefaultPageSize();
    }

    function toggleCriteriaPopupVisibility(isOpen: boolean): void {
        sessionStore.dispatch(LocalOperations.Portfolio.PreventiveRecallList_PreventiveCareCriteria_Visibility, { isOpen });
    }

    function toggleBonusReportPopupVisibility(isOpen: boolean): void {
        sessionStore.dispatch(LocalOperations.Portfolio.PreventiveRecallList_GenerateBonusReport_Visibility, { isOpen });
    }

    function getUpcomingPreventiveCareNoticeLabel(type: PreventiveCareNoticeLetterTypeEnum): string {
        switch (type) {
            case PreventiveCareNoticeLetterTypeEnum.None:
                return getText("RecallList_PreventiveCareNoticeType_FirstLetter");
            case PreventiveCareNoticeLetterTypeEnum.FirstLetter:
                return getText("RecallList_PreventiveCareNoticeType_SecondLetter");
            case PreventiveCareNoticeLetterTypeEnum.SecondLetter:
            case PreventiveCareNoticeLetterTypeEnum.PhoneCall:
                return getText("RecallList_PreventiveCareNoticeType_PhoneCall");
            default:
                return "";
        }
    }

    function getPatientAge(dateOfBirth: Date): string {
        const dobString = LuxonDateTimeParser.parse(dateOfBirth).toFormat("yyyy/LL/dd");
        return PatientHelper.getFormattedAge(new Date(dobString));
    }

    function handlePhoneCallNoticeOk(date: Date): void {
        const requestData = new PreventiveCareNotice({ ...selectedNotice, GeneratedDate: date });
        sessionStore.dispatch(LocalOperations.Portfolio.PreventiveRecallList_UpdateLastCommunicationDate, { preventiveCareNotices: [requestData] });
        setSelectedNotice(null);
    }

    function generateBonusReport(criteriaId: Guid, providerId: Guid): void {
        sessionStore.dispatch(LocalOperations.Portfolio.PreventiveRecallList_OnGenerateBonusReportClick, { criteriaId, providerId });
    }

    function renderDefaultContent(): JSX.Element {
        return (
            <>
                <CommonTableHeaderComponent
                    title={getText("RecallListHeaderLabel")}
                    searchText={props.model.get("SearchText")}
                    searchPlaceholder={getText("SearchLabel")}
                    onSearch={text => sessionStore.dispatch(LocalOperations.Portfolio.PreventiveRecallList_Load_From_Search, { searchText: text })}
                    onRefreshClick={() => sessionStore.dispatch(LocalOperations.Portfolio.PreventiveRecallList_Reload)}
                    onSettingsClick={() => sessionStore.dispatch(LocalOperations.Portfolio.PreventiveRecallList_SearchSettings_Container_Visibility, { isOpen: true })}
                />
                <div id="posref" className="tb ws-scrollable">
                    {props.model.get("IsSettingsContainerOpen")
                        && (
                            <PreventiveRecallListSettingsContainer
                                className="filter-component"
                                onOkCallback={state => sessionStore.dispatch(LocalOperations.Portfolio.PreventiveRecallList_SearchSettings_Load, { settings: state })}
                                savedState={settings}
                                onViewCriteria={() => toggleCriteriaPopupVisibility(true)}
                            />
                        )
                    }
                    <div className="myle-doc-tb main-content table-wrapper">
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="RecallList_main_tb"
                            Columns={tableColumns}
                            DataSource={props.model.get("RecallList")}
                            ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                            RowClickTarget={null}
                            RowClickOperation={null}
                            SaveOptionskeyTarget={saveOptionKey}
                            ColumnSortTarget={MyleTableTarget.Myle_PortfolioPreventiveRecallList_ColumnSortClick}
                        />
                    </div>
                </div>
                <div className="ws-footer table-pager-footer">
                    <div className="row">
                        <div className="col-lg-4 col-md-4 col-sm-4">
                            <SimplePagerControl
                                PageSize={getPageSize()}
                                TotalRows={props.model.get("TotalQueryRows")}
                                CurrentPage={props.model.get("CurrentPage")}
                                EventToDispatchOnPageChange={LocalOperations.Portfolio.PreventiveRecallList_SetPage}
                            />
                        </div>
                        <div className="col-lg-8 col-md-8 col-sm-8 flex-display flex-end">
                            <button
                                disabled={toBeGeneratedLetters.count() == 0}
                                className="btn btn-primary push-right-5"
                                onClick={() => sessionStore.dispatch(LocalOperations.Portfolio.PreventiveRecallList_OnGenerateLettersClick, { letters: toBeGeneratedLetters })}
                            >
                                {getText("PreventiveCare_GenerateLettersLabel")}
                            </button>
                            <button
                                disabled={selectedProviders.length === 0 || eligibleCriteriaList.length === 0}
                                className="btn btn-primary"
                                onClick={() => toggleBonusReportPopupVisibility(true)}
                            >
                                {getText("RecallList_GenerateBonusReportLabel")}
                            </button>
                        </div>
                    </div>
                </div>

                {props.model.get("IsColumnOrderingPopupOpen") && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={columnConfig}
                        SaveOptionskeyTarget={saveOptionKey}
                        ContainerId="columnSettingsPopup"
                        closeColumnOrderPopup={() => sessionStore.dispatch(LocalOperations.Portfolio.PreventiveRecallList_SearchSettings_ColumnOrderingPopup_Visibility, { isOpen: false })}
                    />
                )}

                {props.model.get("IsPreventiveCareCriteriaPopupOpen") && (
                    <PreventiveCareCriteriaPopup onClose={() => toggleCriteriaPopupVisibility(false)} />
                )}

                {props.model.get("IsGenerateBonusReportPopupOpen") && (
                    <GenerateBonusReportPopup
                        providers={selectedProviders}
                        criteriaList={eligibleCriteriaList}
                        onClose={() => toggleBonusReportPopupVisibility(false)}
                        onGenerate={(criteriaId, providerId) => generateBonusReport(criteriaId, providerId)}
                    />
                )}

                {selectedNotice && (
                    <PhoneCallNoticePopup
                        onClose={() => setSelectedNotice(null)}
                        onOk={date => handlePhoneCallNoticeOk(date)}
                    />
                )}

                {props.model.get("IsGenerateBonusReportLoading") && (
                    <Modal
                        showX
                        showOverlay
                        centerContent
                        disableAutoScroll
                        top={2}
                        onClose={() => sessionStore.dispatch(LocalOperations.Portfolio.PreventiveRecallList_InterruptGenerateBonusReportLoading)}
                    >
                        <div className="p-body">
                            <div>
                                <strong>{cacheStore.GetString("RequestIsBeingProceedLabel")}</strong>
                                <div>
                                    <img src={img_loading} alt="loading" />
                                </div>
                            </div>
                        </div>
                    </Modal>
                )}
            </>
        );
    }

    function getContentElement(): JSX.Element {
        switch (currentTab) {
            case PreventiveRecallTab.CorrespondenceHistory:
                return (
                    <CorrespondenceHistory preventiveCareNotices={props.model.get("PreventiveCareNotices")} />
                );

            case PreventiveRecallTab.Default:
            default:
                return renderDefaultContent();
        }
    }

    useEffect(() => {
        $(window).trigger("resize");
    }, [currentTab]);

    return (
        <div className="workspace">
            {getContentElement()}
        </div>
    );
}

export { PreventiveRecallList };
